'use client'

import React from 'react'
import { cva } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type { VariantProps } from 'class-variance-authority'
import { cn } from '@/utils'

const buttonVariants = cva(
  'inline-flex items-center cursor-pointer justify-center font-medium transition-all rounded-full aria-disabled:pointer-events-none disabled:cursor-default',
  {
    variants: {
      variant: {
        primary:
          'bg-gray-600 text-white border border-gray-600 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-400 aria-disabled:bg-gray-300 aria-disabled:border-gray-300 aria-disabled:text-gray-400 hover:bg-black active:bg-black active:text-gray-400',
        secondary:
          'bg-transparent text-gray-600 border border-gray-600 disabled:border-gray-400 disabled:text-gray-400 aria-disabled:border-gray-400 aria-disabled:text-gray-400 hover:border-black hover:text-black active:bg-gray-200 active:border-gray-200 active:text-gray-600',
        link: [
          'underline text-gray-500 disabled:text-gray-400 aria-disabled:text-gray-400 hover:text-black active:text-gray-500',
        ],
      },
      size: {
        sm: 'py-nano px-3xs min-h-[40px] text-3xs',
        md: 'py-nano px-3xs min-h-[48px] text-2xs',
        lg: 'p-3xs min-h-[56px] text-xs',
        xl: 'p-3xs min-h-[64px] text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export type ButtonProps<T extends React.ElementType = 'button'> = {
  ref?: React.Ref<HTMLButtonElement>
  as?: T
  asChild?: boolean
  disabled?: boolean
} & VariantProps<typeof buttonVariants> &
  React.ComponentPropsWithoutRef<T>

export const Button = <T extends React.ElementType>({
  as,
  variant = 'primary',
  size,
  asChild = false,
  className,
  disabled,
  ...props
}: ButtonProps<T>) => {
  const Component = asChild ? Slot.Root : as || 'button'

  // Apply accessible fallback disabled props for non-native buttons. Ex: <a> or <div>
  const isNativeButton = Component === 'button'
  const isNonButtonDisabled = !isNativeButton && disabled
  const nonButtonDisabledProps = {
    'aria-disabled': true,
    tabIndex: -1,
  }
  const buttonDisabledProps = { disabled }

  const disabledProps = isNonButtonDisabled
    ? nonButtonDisabledProps
    : buttonDisabledProps

  return (
    <Component
      className={cn(buttonVariants({ variant, size }), className)}
      {...disabledProps}
      {...props}
    />
  )
}
